import { useEffect, useMemo, useRef } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

/**
 * batch 気象 query の area 列を昇順・重複なしに正規化する。並びが render ごとに
 * 揺れても query key が変わらない (= 再取得ループを起こさない) ようにする。
 */
export function useNormalizedAreaCodes(
  areaCodes: readonly AreaCode[],
): AreaCode[] {
  const key = [...new Set(areaCodes)].sort((a, b) => a - b).join(',')
  return useMemo(
    () => (key === '' ? [] : key.split(',').map((c) => Number(c) as AreaCode)),
    [key],
  )
}

/**
 * 同じ対象日の間だけ前の batch 応答を placeholder として出し続ける
 * `placeholderData`。area 列だけが変わった時 (フィルタ変更・summary の出現) は
 * 残ったカードの値を新しい応答まで保ち、対象日が変わった時は前日の値を出さない。
 * `track` には query 結果を毎 render 渡す (placeholder でない応答の日付を覚える)。
 */
export function useSameDatePlaceholder<T>(targetDate: ProtoDate): {
  placeholderData: (previous: T | undefined) => T | undefined
  track: (result: { data: T | undefined; isPlaceholderData: boolean }) => void
} {
  const dateKey = `${targetDate.year}-${targetDate.month}-${targetDate.day}`
  const dataDateRef = useRef<string | undefined>(undefined)
  const resultRef = useRef<{ hasData: boolean; isPlaceholderData: boolean }>({
    hasData: false,
    isPlaceholderData: false,
  })

  useEffect(() => {
    if (resultRef.current.hasData && !resultRef.current.isPlaceholderData) {
      dataDateRef.current = dateKey
    }
  })

  return {
    placeholderData: (previous) =>
      dataDateRef.current === dateKey ? previous : undefined,
    track: (result) => {
      resultRef.current = {
        hasData: result.data !== undefined,
        isPlaceholderData: result.isPlaceholderData,
      }
    },
  }
}

/** 応答の各要素を area_code で引ける Map にする (要求した area だけを残す)。 */
export function indexByArea<T extends { areaCode: AreaCode }>(
  items: readonly T[] | undefined,
  requested: readonly AreaCode[],
): Map<AreaCode, T> {
  const wanted = new Set(requested)
  const byArea = new Map<AreaCode, T>()
  for (const item of items ?? []) {
    if (wanted.has(item.areaCode)) {
      byArea.set(item.areaCode, item)
    }
  }
  return byArea
}
